import React from 'react';
import { getPaginationModel } from './paginationUtils';

const IngredientManager = ({ ingredients, allIngredients, onEdit, onDelete, searchTerm, onSearchChange, page, onPageChange, searchInputRef, selectedIngredientIds, onIngredientSelection, unselectAllIngredients }) => {
  const sortedIngredients = [...ingredients].sort((a, b) => a.name.localeCompare(b.name));
  const pageSize = 5;
  const totalPages = Math.ceil(sortedIngredients.length / pageSize);
  const paginatedIngredients = sortedIngredients.slice((page - 1) * pageSize, page * pageSize);
  const paginationModel = getPaginationModel(page, totalPages);

  const emptyRows = pageSize - paginatedIngredients.length;

  const getOperationIcon = (operation) => {
    switch (operation) {
      case 'AND': return 'add_circle';
      case 'OR': return 'call_split'; // or 'layers', 'dns'
      case 'NOT': return 'remove_circle'; // or 'block'
      default: return 'help';
    }
  };

  const getOperationColor = (operation) => {
    switch (operation) {
      case 'AND': return '#4CAF50';
      case 'OR': return '#2196F3';
      case 'NOT': return '#f44336';
      default: return '#ccc';
    }
  };

  // Determine list availability for messaging, using the filtered list (ingredients prop)
  const isListEmpty = ingredients.length === 0;

  return (
    <div>
      {/* Show controls if there are selected items OR if there are list items OR if searching */}
      {(ingredients.length > 0 || searchTerm.length > 0 || selectedIngredientIds.length > 0) && (
        <>
          <div className="recipe-filter-controls" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              {selectedIngredientIds.length > 0 && <button title="Unselect all ingredients" onClick={unselectAllIngredients} className="unselect-button">Unselect</button>}
              <div className={`search-input-container ${selectedIngredientIds.length > 0 ? 'collapsed' : 'expanded'}`}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search Ingredients"
                  value={searchTerm}
                  onChange={e => onSearchChange(e.target.value)}
                />
                {searchTerm && <button onClick={() => { onSearchChange(''); searchInputRef.current.focus(); }} className="clear-input-button" title="Clear search"><i className="material-icons">close</i></button>}
              </div>
            </div>
            {selectedIngredientIds.length > 0 && (
              <div className="selected-items">
                {selectedIngredientIds.map(filter => {
                  // Use allIngredients to look up the selected item so it is found even if filtered out from 'ingredients'
                  const actualList = allIngredients || ingredients;
                  const ingredient = actualList.find(i => i.id === filter.id);
                  if (!ingredient) return null;
                  return (
                    <div key={filter.id} className="selected-item" style={{ backgroundColor: getOperationColor(filter.operation) }}>
                      <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{filter.operation}:</span>
                      {ingredient.name}
                      <button onClick={() => onIngredientSelection(filter.id, filter.operation)} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {isListEmpty && searchTerm.length === 0 && selectedIngredientIds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No ingredients found.<br />Add one by using the + button.</div>
      ) : null}

      {paginatedIngredients.length === 0 && searchTerm.length > 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No ingredients found for "{searchTerm}".</div>
      ) : (
        <>
          <ul className="cookbook-list" data-tour="ingredients-list">
            {paginatedIngredients.map(ingredient => {
              const activeFilter = selectedIngredientIds.find(f => f.id === ingredient.id);
              const isSelected = !!activeFilter;

              return (
                <li key={ingredient.id} data-tour={ingredient.isTemporary ? `ingredient-${ingredient.id}` : null} className={isSelected ? 'selected' : ''} style={{ cursor: 'default' }}>
                  <span onClick={() => onIngredientSelection(ingredient.id)} style={{ flexGrow: 1, cursor: 'pointer' }}>{ingredient.name}</span>

                  <div className="item-actions" style={{ marginRight: '10px' }}>
                    {/* AND Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onIngredientSelection(ingredient.id, 'AND'); }}
                      className="icon-button"
                      title="Include (AND)"
                      style={{ color: (activeFilter?.operation === 'AND') ? '#4CAF50' : '#555' }}
                    >
                      <i className="material-icons">add_circle</i>
                    </button>

                    {/* OR Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onIngredientSelection(ingredient.id, 'OR'); }}
                      className="icon-button"
                      title="Include (OR)"
                      style={{ color: (activeFilter?.operation === 'OR') ? '#2196F3' : '#555' }}
                    >
                      <i className="material-icons">call_split</i>
                    </button>

                    {/* NOT Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onIngredientSelection(ingredient.id, 'NOT'); }}
                      className="icon-button"
                      title="Exclude (NOT)"
                      style={{ color: (activeFilter?.operation === 'NOT') ? '#f44336' : '#555' }}
                    >
                      <i className="material-icons">remove_circle</i>
                    </button>
                  </div>

                  <div className="item-actions">
                    <button onClick={(e) => { e.stopPropagation(); onEdit(ingredient); }} className="icon-button" title="Edit ingredient"><i className="material-icons">edit</i></button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(ingredient.id); }} className="icon-button delete-button" title="Delete ingredient"><i className="material-icons">delete</i></button>
                  </div>
                </li>
              );
            })}
            {page === totalPages && totalPages > 1 && emptyRows > 0 && [...Array(emptyRows)].map((_, index) => (
              <li key={`empty-${index}`} style={{ visibility: 'hidden' }}>
                &nbsp;
                <div className="item-actions empty-row-actions">
                  <button className="icon-button" title="Edit ingredient"><i className="material-icons">edit</i></button>
                  <button className="icon-button delete-button" title="Delete ingredient"><i className="material-icons">delete</i></button>
                </div>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="icon-button" title="Previous page"><i className="material-icons">keyboard_arrow_left</i></button>
              <div className="page-numbers">
                {paginationModel.map((item, index) => (
                  <button key={index} onClick={() => onPageChange(item)} disabled={item === '...' || item === page} className={item === page ? 'active' : ''} title={`Go to page ${item}`}>
                    {item}
                  </button>
                ))}
              </div>
              <button onClick={() => onPageChange(page + 1)} disabled={paginatedIngredients.length < pageSize || page * pageSize >= ingredients.length} className="icon-button" title="Next page"><i className="material-icons">keyboard_arrow_right</i></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IngredientManager;